import React, {useEffect, useState} from 'react';
import {FlatList, View} from 'react-native';
import {useDispatch, useSelector} from 'react-redux';

import {fetchVolumePerformanceUserList} from '/redux/action/volumePerformanceReport.action';
import {
  getResultsCount,
  getVolumePerformanceData,
  getVolumePerformanceFilters,
  getVolumePerformanceSort,
  isEndReached as isEndReachedSelector,
  isFetchingNextPage as isFetchingNextPageSelector,
  isListLoading as isListLoadingSelector,
} from '/redux/selector/volumePerformanceReport.selector';

import {ORDER_CATEGORIES} from '../constants/volumePerformanceReport.constants';
import {VolumePerformanceDataRendererProps} from '../types/VolPerfComponent.types';
import {VolumePerformanceUser} from '../types/volumePerformanceReport.types';

import VolPerfEmptyState from './VolPerfEmptyState';
import VolPerUserCard from './VolPerUserCard';
import VolPerUserCardLoading from './VolPerUserCardLoading';

const LOADING_ITEMS_COUNT = 5;

const VolumePerformanceDataRenderer: React.FC<
  VolumePerformanceDataRendererProps
> = ({ListHeaderComponent, VolPerfUserId, searchValue, onScroll}) => {
  const dispatch = useDispatch<any>();
  const isListLoading = useSelector(isListLoadingSelector);
  const isFetchingNextPage = useSelector(isFetchingNextPageSelector);
  const isEndReached = useSelector(isEndReachedSelector);
  const data = useSelector(getVolumePerformanceData);
  const resultsCount = useSelector(getResultsCount);
  const filters = useSelector(getVolumePerformanceFilters);
  const sort = useSelector(getVolumePerformanceSort);
  const isScheduledResult =
    filters?.orderCategory === ORDER_CATEGORIES.TEMPLATES;

  // Local state to track page number for pagination
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [filters, sort, searchValue]);

  const renderItem = ({item}: {item: VolumePerformanceUser}) => (
    <VolPerUserCard
      orderId={item.orderId}
      firstName={item.customer.firstName}
      lastName={item.customer.lastName}
      customerTypeId={item.customer.customerTypeId}
      isPremierCustomer={item.customer.isPremierCustomer}
      orderType={item.orderType}
      orderStatus={item.orderStatus}
      creationDate={item.creationDate}
      nextOrderDate={item.nextOrderDate}
      lastOrderDate={item.lastOrderDate}
      orderNumber={item.orderNumber}
      referenceNumber={item.referenceNumber}
      isScheduledResult={isScheduledResult}
      qv={item.qv}
      consumableSubTotal={item.consumableSubTotal}
      totalPrice={item.totalPrice}
      currency={item.currency}
    />
  );

  /**
   * Triggers when user scrolls near the bottom of the list.
   */
  const handleEndReached = () => {
    const hasLoadedAllRecords =
      resultsCount > 0 && data.length >= Number(resultsCount);

    if (
      !isFetchingNextPage &&
      !isEndReached &&
      !isListLoading &&
      !hasLoadedAllRecords
    ) {
      const nextPage = page + 1;
      setPage(nextPage);
      dispatch(
        fetchVolumePerformanceUserList(nextPage, VolPerfUserId, searchValue),
      );
    }
  };

  /**
   * Renders the loading skeleton for initial load and pagination.
   */
  const renderFooter = () => {
    if (isListLoading) {
      return (
        <View>
          {Array.from({length: LOADING_ITEMS_COUNT}).map((_, index) => (
            <VolPerUserCardLoading key={`initial-loading-${index}`} />
          ))}
        </View>
      );
    }
    if (isFetchingNextPage) {
      return <VolPerUserCardLoading />;
    }
    return null;
  };

  return (
    <FlatList
      testID="volume-perf-list"
      data={isListLoading ? [] : data}
      renderItem={renderItem}
      keyExtractor={(item, index) =>
        `${item.orderId}-${item.orderNumber}-${index}`
      }
      ListHeaderComponent={ListHeaderComponent}
      ListFooterComponent={renderFooter}
      ListEmptyComponent={!isListLoading ? <VolPerfEmptyState /> : null}
      onEndReached={handleEndReached}
      onEndReachedThreshold={0.5} // Trigger load when 50% from bottom
      contentContainerStyle={{paddingBottom: 20}}
      keyboardShouldPersistTaps="handled"
      onScroll={onScroll}
      scrollEventThrottle={16}
    />
  );
};

export default VolumePerformanceDataRenderer;
